import {
  Badge,
  Menu,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { ChevronDown } from 'lucide-react';

import { useEnvironment } from '../context/EnvironmentContext';
import { SURFACE } from '../theme/tokens';

// FD-421 / FD-ADR-040 §8 — which deployment am I acting on.
//
// This fills the slot ADR-031 §1 reserved and `TopBar.tsx` argued for while
// refusing to ship it: a mock dropdown would have been worse than nothing, and
// the missing piece was never the control, it was a definition of "environment"
// that came from somewhere real. FD-420's `/config.json` supplies it.
//
// It is a DEPLOYMENT selector, not the tenant/workspace selector ADR-015
// deferred and ADR-031 §1 kept deferred. A Model B environment's tenants are an
// allow-list ON that environment; they are not a thing the console switches
// between. The two must not be conflated.
//
// ONE ENVIRONMENT RENDERS NOTHING. That is the Mode 1 no-change guarantee, and
// it is the reason the component starts by returning null rather than by
// rendering a disabled control: a customer-deployed console has exactly one
// deployment, so a selector there would be chrome describing a choice that does
// not exist. A single environment that sets `tone` opts back in to the INDICATOR
// alone — no menu, nothing to click.
//
// ── FD-586: FORBIDDEN IS HIDDEN, NOT GREYED ───────────────────────────────
//
// The Air Bridge fleet console is one public page in front of many customers'
// isolated environments. FD-422 listed all of them and greyed the ones this
// tenant may not reach — so every customer's admin read every OTHER customer's
// environment, labelled "No access from this tenant". That is the console
// describing the fleet when the operator asked about their own deployment, and
// it is a disclosure the Manager's allow-list (the actual boundary, unchanged)
// never made. So the menu lists `visibleEnvironments`: everything not refused
// with `403 FORBIDDEN_TENANT`.
//
// `unknown` STAYS LISTED, and that is the half of FD-422 this keeps. An
// environment whose probe failed for any other reason — down, timed out, CORS —
// is not one the operator lacks access to, and for Rubix staff the difference
// between "down" and "not permitted" is the difference between raising an
// incident and asking for consent. It renders under whatever label resolved —
// for a label-less config that is the id, which is the honest short code.
//
// ONE VISIBLE ENVIRONMENT COLLAPSES TO THE MODE 1 SHAPE. A customer admin on a
// fifty-environment fleet who may reach exactly one has no choice to make, so
// they get what a single-environment console renders: the tone badge or nothing.
// `hasEnvironmentChoice` carries that; `isSingleEnvironment` (the CONFIGURED
// count) still governs the URL and is deliberately not consulted here.

const useStyles = makeStyles({
  // Fluent's subtle Button is drawn for a light surface: its rest foreground and
  // its hover fill both disappear on the near-black bar. Inverted here with
  // makeStyles rather than Tailwind, per the addendum's styling rule — a utility
  // class on a Fluent component either does nothing or breaks on upgrade.
  //
  // The hover fill matches UserProfile's 8% white exactly, so the two controls
  // that sit side by side in the bar respond identically.
  trigger: {
    color: SURFACE.topBarText,
    ':hover': {
      color: '#FFFFFF',
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    ':hover:active': {
      color: '#FFFFFF',
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  // FD-456 — the LIGHT-chrome variant, for the AirBridge preview's white top
  // bar. Empty on purpose: Fluent's own subtle Button is already drawn for a
  // light surface, so the correct light rendering is the one with NO inversion.
  // It exists as a named class so the `surface` prop reads as a choice between
  // two treatments rather than as `surface === 'dark' && styles.trigger`, which
  // is the form that quietly acquires a third state later.
  triggerLight: {},
});

/**
 * Present a `tone` as a badge.
 *
 * `production` is the only tone that raises its voice, and it is AMBER, not red:
 * the console's status vocabulary reads red as "this is broken" and amber as
 * "a human should be paying attention" (FLUENT_UI_ADDENDUM). Being on production
 * is not a failure — it is the thing to notice before you act.
 *
 * Every other tone renders neutral, INCLUDING one nobody anticipated. The
 * vocabulary is open by construction — `consoleConfig.ts` carries `tone` without
 * interpreting it — so neutral is the handled default here rather than a value
 * falling through, which is the distinction FD-197 was about.
 */
function toneAppearance(tone: string): 'warning' | 'subtle' {
  return tone.trim().toLowerCase() === 'production' ? 'warning' : 'subtle';
}

function toneLabel(tone: string): string {
  const trimmed = tone.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function ToneBadge({ tone }: { tone: string }) {
  return (
    <Badge appearance="tint" color={toneAppearance(tone)} size="small">
      {toneLabel(tone)}
    </Badge>
  );
}

export interface EnvironmentSelectorProps {
  /**
   * Which chrome the selector is sitting on (FD-456).
   *
   * `dark` — the ADR-031 near-black top bar. THE DEFAULT, so every existing call
   * site renders byte-identically and the production console is untouched.
   * `light` — the AirBridge preview's white top bar, where Fluent's own subtle
   * Button is already correct and the dark inversion would be unreadable.
   */
  surface?: 'dark' | 'light';
}

export default function EnvironmentSelector({ surface = 'dark' }: EnvironmentSelectorProps = {}) {
  const styles = useStyles();
  const triggerClass = surface === 'light' ? styles.triggerLight : styles.trigger;
  const {
    environment,
    environmentId,
    setEnvironmentId,
    visibleEnvironments,
    hasEnvironmentChoice,
    noAccessibleEnvironment,
  } = useEnvironment();

  // Nothing this operator may reach: no selector and no badge. Naming the active
  // environment here would name one that has refused them — the zero-access
  // message in the content column is the whole answer.
  if (noAccessibleEnvironment) return null;

  if (!hasEnvironmentChoice) {
    // Nothing to choose between. A tone still earns an indicator — a
    // single-environment PRODUCTION console is exactly where "notice where you
    // are" is worth one chip — but there is no control and no menu.
    return environment.tone ? <ToneBadge tone={environment.tone} /> : null;
  }

  return (
    <div className="flex items-center gap-2">
      <Menu
        positioning={{ position: 'below', align: 'end' }}
        checkedValues={{ environment: [environmentId] }}
        onCheckedValueChange={(_event, data) => {
          // Radio semantics do not uncheck on re-click, but read defensively:
          // an empty array must never be interpreted as "switch to undefined".
          const next = data.checkedItems[0];
          if (next && next !== environmentId) setEnvironmentId(next);
        }}
      >
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            size="small"
            className={triggerClass}
            icon={<ChevronDown size={14} strokeWidth={1.5} />}
            iconPosition="after"
            aria-label={`Environment: ${environment.label}. Change environment`}
          >
            {environment.label}
          </Button>
        </MenuTrigger>
        <MenuPopover>
          {/* MenuItemRadio rather than MenuItem + a tick: it is single-select
              from a fixed set, so the primitive carries role="menuitemradio"
              and aria-checked for free. Fluent primitives first. */}
          <MenuList>
            {visibleEnvironments.map((candidate) => (
              <MenuItemRadio
                key={candidate.id}
                name="environment"
                value={candidate.id}
                // The deployment identifier, shown because an operator checking
                // which environment they are in wants the identifier, not just
                // the label someone typed. Non-secret configuration (the
                // FD-ADR-023 §5 disclosure posture).
                secondaryContent={candidate.customerEnvironmentId}
              >
                {candidate.label}
              </MenuItemRadio>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
      {environment.tone && <ToneBadge tone={environment.tone} />}
    </div>
  );
}
